/* eslint-disable */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import muiTheme from './theme/muiTheme';
import { AuthProvider, useAuth } from './Contexts/AuthContext';
import { DrawerProvider } from './Contexts/DrawerContext';
import { ToastProvider } from './context/ToastContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Routers from './Routes/routers';
import { LoginPage } from './Pages/LoginPage/LoginPage';
import { RegisterPage } from './Pages/Landing/RegisterPage';
import { ForgotPasswordPage } from './Pages/Landing/ForgotPasswordPage';
import { MyProperties } from './Pages/PropertyDetails/MyProperties';
import { PropertiesUnderVerification } from './Pages/PropertyDetails/PropertiesUnderVerification';
import { SavedProperties } from './Pages/PropertyDetails/SavedProperties';
import { PropertyDetailsPage } from './Pages/PropertyDetails/PropertyDetailsPage';
import { MyProfile } from './Pages/MyProfile/MyProfile';
import { AdminPortal } from './Pages/AdminPortal/AdminPortal';
import { initAnalytics, trackPageView } from './Utils/analytics';
import './App.css';

// Login wrapper with navigation logic
function LoginWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, loading, login } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleLoginSuccess = async (email, password) => {
    const result = await login(email, password);
    if (result && result.success) {
      const loginState = location.state || {};
      const from = loginState.from || '/';
      const { from: _ignoredFrom, ...postLoginState } = loginState;
      navigate(from, { state: postLoginState });
    }
    return result;
  };

  return (
    <LoginPage
      onLoginSuccess={handleLoginSuccess}
      onSwitchToRegister={() => navigate('/register')}
      onBackToHome={() => navigate('/')}
      onForgotPassword={() => navigate('/forgot-password')}
    />
  );
}

// Register wrapper with navigation logic
function RegisterWrapper() {
  const navigate = useNavigate();
  const { isLoggedIn, loading, register } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleRegisterSuccess = async (userData) => {
    const result = await register(userData);
    if (result && result.success) {
      navigate('/');
    }
    return result;
  };

  return (
    <RegisterPage
      onRegisterSuccess={handleRegisterSuccess}
      onSwitchToLogin={() => navigate('/login')}
      onBackToHome={() => navigate('/')}
    />
  );
}

// Forgot password wrapper
function ForgotPasswordWrapper() {
  const navigate = useNavigate();
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <ForgotPasswordPage
      onBackToLogin={() => navigate('/login')}
      onBackToHome={() => navigate('/')}
    />
  );
}

// My Properties wrapper with auth protection
function MyPropertiesWrapper() {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <MyProperties />;
}

// All Properties wrapper with auth + admin protection
function AllPropertiesWrapper() {
  const { isLoggedIn, loading, user, userProfile } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  const roleId = user?.role_id ?? user?.roleId ?? userProfile?.role_id ?? userProfile?.roleId;
  const isAdmin = Number(roleId) === 1;

  if (!isAdmin) {
    return <Navigate to="/my-properties" replace />;
  }

  return <Navigate to="/admin-portal/listings" replace />;
}

// Saved Properties wrapper
function SavedPropertiesWrapper() {
  return <SavedProperties />;
}

// My Profile wrapper with auth protection
function MyProfileWrapper() {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <MyProfile />;
}

function AdminPortalEntryWrapper() {
  const { isLoggedIn, loading, user, userProfile } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  const roleId = user?.role_id ?? user?.roleId ?? userProfile?.role_id ?? userProfile?.roleId;
  const isAdmin = Number(roleId) === 1;

  if (!isAdmin) {
    return <Navigate to="/my-properties" replace />;
  }

  return <Navigate to="/admin-portal/dashboard" replace />;
}

function AdminPortalDashboardWrapper() {
  const { isLoggedIn, loading, user, userProfile } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  const roleId = user?.role_id ?? user?.roleId ?? userProfile?.role_id ?? userProfile?.roleId;
  const isAdmin = Number(roleId) === 1;

  if (!isAdmin) {
    return <Navigate to="/my-properties" replace />;
  }

  return <AdminPortal />;
}
function SeoSearchRedirect() {
  const { searchSlug } = useParams();
  const navigate = useNavigate();

  const decodeSlugToQuery = (value) => {
    if (!value) return '';
    try {
      const restoredSpaces = String(value).replace(/~/g, '%20');
      return decodeURIComponent(restoredSpaces).trim();
    } catch (error) {
      return String(value).replace(/~/g, ' ').trim();
    }
  };

  const query = decodeSlugToQuery(searchSlug);

  React.useEffect(() => {
    navigate('/', {
      replace: true,
      state: query ? { seoSearchQuery: query } : undefined,
    });
  }, [navigate, query]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}

function AnalyticsTracker() {
  const location = useLocation();

  React.useEffect(() => {
    initAnalytics();
  }, []);

  React.useEffect(() => {
    const pagePath = `${location.pathname}${location.search}`;
    trackPageView(pagePath);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className='App'>
        <Router>
          <AnalyticsTracker />
          <ToastProvider>
            <AuthProvider>
              <DrawerProvider>
                <Routes>
                  {/* New authentication and user routes */}
                  <Route path="/login" element={<LoginWrapper />} />
                  <Route path="/register" element={<RegisterWrapper />} />
                  <Route path="/forgot-password" element={<ForgotPasswordWrapper />} />
                  <Route path="/my-properties" element={<MyPropertiesWrapper />} />
                  <Route path="/admin-portal" element={<AdminPortalEntryWrapper />} />
                  <Route path="/admin-portal/dashboard" element={<AdminPortalDashboardWrapper />} />
                  <Route path="/admin-portal/users" element={<AdminPortalDashboardWrapper />} />
                  <Route path="/admin-portal/listings" element={<AdminPortalDashboardWrapper />} />
                  <Route path="/admin-portal/brokers" element={<AdminPortalDashboardWrapper />} />
                  <Route path="/all-properties" element={<AllPropertiesWrapper />} />
                  <Route path="/saved-properties" element={<SavedPropertiesWrapper />} />
                  <Route path="/my-profile" element={<MyProfileWrapper />} />
                  <Route path="/search/:searchSlug" element={<SeoSearchRedirect />} />
                  <Route path="/property/:id" element={<PropertyDetailsPage />} />
                  <Route path="/property/draft/:id" element={<PropertyDetailsPage />} />
                  
                  {/* Existing routes from routers.js */}
                  <Route path="/*" element={<Routers />} />
                </Routes>
                <ToastContainer />
              </DrawerProvider>
            </AuthProvider>
          </ToastProvider>
        </Router>
      </div>
    </ThemeProvider>
  );
}

export default App;
